import type { Dayjs } from 'dayjs';
import type * as Responses from 'endpoints.tenders';
import * as api from './api';
import * as analytics from './analytics';

export async function overview(solution: string) {
  return await api.get<Responses.Overview>({
    url: '/tenders',
    params: { solution, limit: 50, status: ['planning', 'active'] },
  });
}

export async function pollOverview(solution: string, since: Dayjs) {
  return await api.poll<Responses.Overview>({
    url: '/tenders',
    params: { solution, limit: 50, status: ['planning', 'active', 'finished'] },
    since,
  });
}

export async function history(solution: string, page: number) {
  return await api.get<Responses.Overview>({
    url: '/tenders',
    params: { solution, page, limit: 50, status: 'archived', sort: '-finish' },
  });
}

export async function tender(id: string) {
  return await api.get<Responses.TenderGet>({
    url: `/tenders/${id}`,
    method: 'GET',
  });
}

export async function order(tenderId: string, orderId: string) {
  return await api.get<Responses.OrderGet>({
    url: `/tenders/${tenderId}/offers/${orderId}`,
    method: 'GET',
  });
}

export async function pollCounters(id: string, since: Dayjs) {
  return await api.poll<Responses.CountersPoll>({
    url: `/tenders/${id}/bids`,
    method: 'GET',
    since,
  });
}

export async function getCounters(tender: string) {
  return await api.get<Responses.CountersPoll>({
    url: `/tenders/${tender}/bids`,
    method: 'GET',
  });
}

export async function counter(
  tenderId: string,
  orderId: string,
  formdata: FormData,
  id?: string
) {
  const url =
    `/tenders/${tenderId}/offers/${orderId}/bids` + (id ? `/${id}` : '');
  const method = id ? 'PUT' : 'POST';
  return await api.modify<Responses.CounterGet>({ url, formdata, method });
}

export async function deleteCounter(
  tender: string,
  order: string,
  counter: string
) {
  return await api.remove<null>({
    url: `/tenders/${tender}/offers/${order}/bids/${counter}`,
    method: 'DELETE',
  });
}

export async function quickbid(
  tenderId: string,
  orderId: string,
  counterId: string,
  price: number
) {
  return await api.modify<Responses.CounterGet>({
    url: `/tenders/${tenderId}/offers/${orderId}/bids/${counterId}`,
    method: 'PATCH',
    data: { price, quickbid: true },
  });
}

export async function copyTenderPreview(
  id: string,
  params: { start?: string; cadence?: 'weekly' | 'monthly' } = {}
) {
  return await api.get<Responses.TenderCopyPreview>({
    url: `/tenders/${id}/copy`,
    method: 'GET',
    params,
  });
}

export async function copyTender(
  id: string,
  data: { start?: string; cadence?: 'weekly' | 'monthly' }
) {
  return await api.modify<Responses.TenderGet>({
    url: `/tenders/${id}/copy`,
    method: 'POST',
    data,
  });
}

export async function editTender(formdata: FormData) {
  const id = formdata.get('id');
  const url = id ? `/tenders/${id}` : '/tenders';
  const method = id ? 'PUT' : 'POST';
  return await api.modify<Responses.TenderGet>({ url, method, formdata });
}

export async function editTenderFixedPrice(formdata: FormData) {
  const id = formdata.get('id');
  const url = `/tenders/${id}/fixed-index`;
  const method = 'POST';
  return await api.modify<Responses.TenderGet>({ url, method, formdata });
}

export async function editOrder(
  formdata: FormData,
  tenderId: string,
  orderId?: string
) {
  const url = `/tenders/${tenderId}/offers` + (orderId ? '/' + orderId : '');
  return await api.modify<Responses.OrderGet>({
    url,
    method: orderId ? 'PUT' : 'POST',
    formdata,
  });
}

export async function publish(tender: string, tendervisible?: boolean) {
  return await api.modify<Responses.TenderGet>({
    url: `/tenders/${tender}`,
    method: 'PATCH',
    data: { status: 'active', tendervisible },
  });
}

export async function editVisibility(tender: string, tendervisible: boolean) {
  return await api.modify<Responses.TenderGet>({
    url: `/tenders/${tender}`,
    method: 'PATCH',
    data: { tendervisible },
  });
}

export async function notify(tender: string) {
  return await api.modify<Responses.TenderGet>({
    url: `/tenders/${tender}`,
    method: 'PATCH',
    data: { status: 'notify' },
  });
}

export async function trade(
  tender: string,
  order: string,
  counter: string,
  data: { volume: number }
) {
  return await api.modify<Responses.CounterTrade>({
    url: `/tenders/${tender}/offers/${order}/bids/${counter}`,
    method: 'PATCH',
    data,
  });
}

export async function finalise(tender: string) {
  return await api.modify<Responses.TenderGet>({
    url: `/tenders/${tender}`,
    method: 'PATCH',
    data: { status: 'archived' },
  });
}

export async function counterSchema(
  tenderId: string,
  orderId: string,
  counterId?: string
) {
  const url =
    `/tenders/${tenderId}/offers/${orderId}/bids` +
    (counterId ? `/${counterId}` : '') +
    '/schema';
  return await api.get<Responses.Schema>({ url, method: 'GET' });
}

export type TenderofferPricingParams = {
  division?: string;
  warehouse?: string;
  variant?: string;
  price?: number | string;
  volume?: number | string;
  fixed_index?: boolean;
};

export async function tenderofferPricing(
  tenderId: string,
  orderId: string,
  params: TenderofferPricingParams = {}
) {
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null)
  );
  return await api.get<Responses.TenderofferPricing>({
    url: `/tenders/${tenderId}/offers/${orderId}/pricing`,
    method: 'GET',
    params: cleaned,
  });
}

export async function tenderSchema(solution: string, tenderId?: string) {
  const url = tenderId ? `/tenders/${tenderId}/schema` : '/tenders/schema';
  const params = { solution };
  return await api.get<Responses.Schema>({ url, method: 'GET', params });
}

export async function fixedPriceSchema(tenderId: string) {
  const url = `/tenders/${tenderId}/fixed-index/schema`;
  return await api.get<Responses.Schema>({ url, method: 'GET' });
}

export async function orderSchema(
  tender: string,
  product: string,
  order?: string
) {
  const url =
    `/tenders/${tender}/offers` + (order ? `/${order}` : '') + '/schema';
  return await api.get<Responses.Schema>({
    url,
    method: 'GET',
    params: { product },
  });
}

export async function counterHistory(tenderId: string, orderId: string) {
  const url = `/tenders/${tenderId}/offers/${orderId}/bids/history`;
  return await api.get<Responses.CounterHistory>({ url, method: 'GET' });
}

export async function buyers(tenderId: string, orderId: string) {
  const url = `/tenders/${tenderId}/offers/${orderId}/displayed-prices`;
  return await api.get<Responses.OrderBuyers>({ url, method: 'GET' });
}

export async function participants(tenderId: string) {
  const url = `/tenders/${tenderId}/participants`;
  return await api.get<Responses.ParticipantsGet>({ url, method: 'GET' });
}

export async function setParticipant(
  tender: string,
  participant: string,
  value: boolean
) {
  const url = `/tenders/${tender}/participants/${participant}`;
  const method = value ? 'POST' : 'DELETE';
  return await api.modify<Responses.Participant>({ url, method });
}

export async function setParticipantGroup(
  tender: string,
  participants: string[]
) {
  return await api.modify<Responses.ParticipantsGet>({
    url: `/tenders/${tender}/participants`,
    method: 'PUT',
    data: { participants },
  });
}

export async function deleteOrder(tender: string, order: string) {
  return await api.remove<Responses.OrderGet>({
    url: `/tenders/${tender}/offers/${order}`,
    method: 'DELETE',
  });
}

export async function cancelTender(tender: string) {
  return await api.remove<Responses.TenderGet>({
    url: `/tenders/${tender}`,
    method: 'DELETE',
  });
}

export async function activity(tender: string) {
  return await api.get<Responses.Activity>({
    url: `/tenders/${tender}/activity`,
  });
}

export async function reports(tender: string) {
  return await analytics.get<Responses.Detailed>({
    url: `/tender/${tender}/detailed`,
  });
}

export async function reportCounterHistory(tender: string) {
  return await analytics.get<Responses.Bidding>({
    url: `/tender/${tender}/bidding`,
  });
}

export async function getParticipantsCSV(tender: string, viewed: boolean) {
  const params: { [key: string]: boolean } = {};

  if (viewed) {
    params.viewed = true;
  }

  return await analytics.get({
    url: `/tender/${tender}/participants.csv`,
    params: params,
  });
}
